import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { ChevronLeft, Ellipsis, Trash2, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { DeleteDialog } from '@/components/shared/delete-dialog';
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { MultiSelect } from '@/components/ui/multi-select';
import {
	useResponseStrategyTemplate,
	useUpdateResponseStrategyTemplateEditableFields,
	useDeleteResponseStrategyTemplate
} from '@/hooks/use-response-strategy-templates';
import { usePermissions } from '@/hooks/use-permissions';
import { usePublishedCSCategories } from '@/hooks/use-cs-categories';
import { toast } from '@/components/ui/sonner';

/**
 * Template detail/edit page. Templates have no draft state and their name +
 * prompt + generated strategy text are fixed (see
 * responseStrategyTemplate.service.ts) — only the intent mapping and
 * enable/disable are editable, and by any account admin (server-side gated
 * with the regular 'edit' permission, per responseStrategyTemplate.routes.ts).
 * Deleting a template stays super-admin only.
 */
export default function ResponseStrategyTemplateForm() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const { isSuperAdmin } = usePermissions();

	const [intents, setIntents] = useState<string[]>([]);
	const [deleteOpen, setDeleteOpen] = useState(false);
	const [showSaveEnableConfirm, setShowSaveEnableConfirm] = useState(false);
	const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

	const loadedIdRef = useRef<string | undefined>(undefined);
	const initialIntentsRef = useRef<string[]>([]);

	const templateQuery = useResponseStrategyTemplate(id || '');
	const template = templateQuery.data;
	const updateMutation = useUpdateResponseStrategyTemplateEditableFields();
	const deleteMutation = useDeleteResponseStrategyTemplate();

	const { data: csCategories = [] } = usePublishedCSCategories();
	const intentOptions = useMemo(() => csCategories.map((cat) => ({ label: cat.name, value: cat.id })), [csCategories]);

	useEffect(() => {
		if (template && loadedIdRef.current !== template.id) {
			loadedIdRef.current = template.id;
			setIntents(template.intents);
			initialIntentsRef.current = template.intents;
		}
	}, [template]);

	const hasUnsavedChanges = () => {
		const snap = initialIntentsRef.current;
		return intents.length !== snap.length || !intents.every((i) => snap.includes(i));
	};

	const handleBack = () => {
		if (hasUnsavedChanges()) {
			setShowDiscardConfirm(true);
		} else {
			navigate('/training/response-strategies');
		}
	};

	const save = async (isActive: boolean) => {
		if (!id) return;
		if (intents.length === 0) {
			toast.error('At least one intent is required');
			return;
		}

		try {
			await updateMutation.mutateAsync({ id, data: { intents, isActive } });
			toast.success('Template saved');
			initialIntentsRef.current = intents;
			templateQuery.refetch();
		} catch (error) {
			const msg = error instanceof Error ? error.message : 'An error occurred';
			toast.error(`Failed to save: ${msg}`);
		}
	};

	const handleSaveEnable = () => setShowSaveEnableConfirm(true);

	const handleDelete = async () => {
		if (!id) return;
		try {
			await deleteMutation.mutateAsync(id);
			toast.success('Template deleted');
			navigate('/training/response-strategies');
		} catch {
			toast.error('Failed to delete template');
		}
	};

	const isSaving = updateMutation.isPending;
	// Intent mapping + enable/disable are open to any account admin; only
	// deleting a template is super-admin only.
	const canEdit = true;
	const canDelete = isSuperAdmin;
	const canSave = intents.length > 0 && canEdit;
	// Only show the action once the intent mapping has actually been edited —
	// there's nothing to save otherwise, active or not.
	const showActionButton = hasUnsavedChanges();

	if (templateQuery.isLoading) {
		return (
			<div className='flex flex-col h-full'>
				<div className='flex items-center gap-3 px-5 py-3 border-b border-gray-500'>
					<Skeleton className='h-8 w-8 rounded-md' />
					<Skeleton className='h-5 w-48' />
					<div className='flex items-center gap-2 ml-auto'>
						<Skeleton className='h-7 w-28 rounded-full' />
					</div>
				</div>
				<div className='flex-1 p-6 space-y-4 max-w-[620px] w-full'>
					<Skeleton className='h-24 w-full rounded-2xl' />
					<Skeleton className='h-40 w-full rounded-2xl' />
				</div>
			</div>
		);
	}

	return (
		<div className='flex flex-col h-full'>
			{/* Header */}
			<div className='flex items-center gap-1 px-4 py-3.5 border-b border-gray-500'>
				<Button variant='ghost' size='icon' className='h-7 w-7' onClick={handleBack}>
					<ChevronLeft className='size-4' />
				</Button>
				<span className='flex-1 text-base font-semibold text-ink'>{template?.name}</span>

				<div className='flex items-center gap-1.5 shrink-0'>
					{showActionButton && (
						<Button size='sm' className='text-xs' onClick={handleSaveEnable} disabled={!canSave || isSaving}>
							{isSaving ? 'Saving...' : 'Save and enable'}
						</Button>
					)}

					{canDelete && (
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button variant='ghost' size='icon' className='h-7 w-7'>
									<Ellipsis size={14} />
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align='end'>
								<DropdownMenuItem onSelect={() => setDeleteOpen(true)} className='text-red-600 focus:text-red-600'>
									<Trash2 size={14} className='mr-2' />
									Delete
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>
					)}
				</div>
			</div>

			{/* Content */}
			<div className='flex-1 overflow-y-auto'>
				<div className='flex flex-col gap-7 max-w-[620px] w-full p-5'>
					{/* Choose when to use the strategy */}
					<div className='flex flex-col gap-3'>
						<p className='text-[13px] font-medium text-gray-800'>Choose when to use the strategy</p>
						<div className='border border-gray-300 rounded-3xl p-3 flex flex-col gap-2'>
							<p className='text-xs font-medium text-gray-900'>Select intents</p>
							<MultiSelect
								options={intentOptions}
								defaultValue={intents}
								onValueChange={setIntents}
								disabled={!canEdit}
								showAllSelected
								placeholder='Select'
								popoverPlaceholder='Search intents...'
								hideSelectAll
							/>
						</div>
					</div>

					{/* Response strategy — prompt and generated text are fixed for templates */}
					<div className='flex flex-col gap-3'>
						<p className='text-[13px] font-medium text-gray-800'>Response strategy</p>
						<div className='border border-gray-300 rounded-3xl p-3 flex flex-col gap-4'>
							<div className='flex flex-col gap-2'>
								<div className='flex items-center gap-1'>
									<span className='text-xs font-medium text-gray-900'>Prompt</span>
									<TooltipProvider>
										<Tooltip>
											<TooltipTrigger asChild>
												<Info size={12} className='text-gray-400 cursor-default' />
											</TooltipTrigger>
											<TooltipContent side='top' align='start' alignOffset={-36} variant='dark' className='max-w-72'>
												This is a pre-built template. You can customize when it should apply by editing its intents,
												but the response instructions themselves can't be changed.
											</TooltipContent>
										</Tooltip>
									</TooltipProvider>
								</div>
								<Textarea
									value={template?.merchantAsk ?? ''}
									readOnly
									className='min-h-[80px] max-h-[400px] rounded-2xl resize-none bg-gray-300 text-gray-700 cursor-default focus-visible:ring-0'
								/>
							</div>

							<div className='border-t border-dashed border-gray-300' />
							<div className='flex flex-col gap-3 pb-1'>
								<div className='flex items-center gap-2'>
									<img src='/assets/persona/ai-sparkle.svg' alt='' className='size-8 shrink-0' />
									<p className='text-sm font-medium text-foreground'>Generated Response Strategy</p>
								</div>
								<p className='text-[13px] text-foreground whitespace-pre-wrap'>{template?.strategy}</p>
								<p className='text-xs italic text-gray-800'>
									The application's limitations shape this response, meaning some prompt aspects may be bypassed.
								</p>
							</div>
						</div>
					</div>
				</div>
			</div>

			<DeleteDialog
				open={deleteOpen}
				onOpenChange={setDeleteOpen}
				onConfirm={handleDelete}
				title='Delete template?'
				description='Deleting this template is permanent. Do you want to continue?'
				isLoading={deleteMutation.isPending}
			/>

			<AlertDialog open={showSaveEnableConfirm} onOpenChange={setShowSaveEnableConfirm}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Enable strategy?</AlertDialogTitle>
						<AlertDialogDescription>
							Once enabled, this particular response strategy will fire whenever the intents are triggered
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Cancel</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => {
								setShowSaveEnableConfirm(false);
								save(true);
							}}
						>
							Enable
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			<AlertDialog open={showDiscardConfirm} onOpenChange={setShowDiscardConfirm}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Discard changes?</AlertDialogTitle>
						<AlertDialogDescription>You have unsaved changes. Are you sure you want to leave?</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Cancel</AlertDialogCancel>
						<AlertDialogAction
							className='bg-destructive text-white border-destructive hover:bg-destructive/90'
							onClick={() => {
								setShowDiscardConfirm(false);
								navigate('/training/response-strategies');
							}}
						>
							Discard
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
}
